import React, {useState} from 'react';
import {Typography, List, useTheme, Box, Stack, Button, Collapse, Divider} from '@mui/material';
import {translations} from 'presentation/translations-new';
import Icon from 'presentation/icons';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';
import type {AppointmentsHoursProps} from './types';
import {FilledCircle, StyledListItem} from './styles';
import {
	parseAppointmentHours,
	currentDayFirstWhenNotExpanded,
} from 'presentation/modules/guide/shared/utils/parseAppointmentHours';

const {guideDetails} = translations['pt-br'].privateFlow.guideFlow;
const {seeMore, seeLess} = translations['pt-br'].commons;

const AppointmentsHours: React.FC<AppointmentsHoursProps> = ({hours, title}) => {
	const theme = useTheme();
	const parsedHours = parseAppointmentHours(hours);
	const [expandedState, setExpandedState] = useState(false);
	const {isEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_HORARIOS_ATENDIMENTO);

	const handleExpandClick = () => {
		setExpandedState(prev => !prev);
	};

	if (!parsedHours?.length || !isEnabled) return null;

	const daysWithCurrentDayFirst = currentDayFirstWhenNotExpanded({
		dateTimes: parsedHours,
		isExpanded: expandedState,
	});

	const currentDay = daysWithCurrentDayFirst.find(day => day.isCurrentDay);
	const TEXT_COLLAPSED_SIZE_WITH_SECOND_SHIFT = 48;
	const TEXT_COLLAPSED_SIZE_WITHOUT_SECOND_SHIFT = 32;
	const textCollapsedSize = currentDay?.secondShift
		? TEXT_COLLAPSED_SIZE_WITH_SECOND_SHIFT
		: TEXT_COLLAPSED_SIZE_WITHOUT_SECOND_SHIFT;

	return (
		<Stack gap={1}>
			<Typography variant="subtitle2" color="text.secondary">
				{title}
			</Typography>
			<Collapse in={expandedState} collapsedSize={textCollapsedSize}>
				<List disablePadding>
					{daysWithCurrentDayFirst.map(hour => {
						const statusColor = hour.isClosed
							? theme.colors.light.error.main
							: theme.colors.light.success.main;
						return (
							<StyledListItem
								key={hour.weekDay}
								sx={{
									alignItems: 'center',
								}}>
								<Box display="flex" alignItems="center" width="100%">
									<FilledCircle sx={{backgroundColor: statusColor}} m={1} />
									<Typography variant="body1">{hour.weekDay}</Typography>
									<Box flexGrow={1} />
									<Typography variant="body1">
										{hour.isClosed ? guideDetails.closed : hour.firstShift}
										<br />
										{hour.secondShift ? hour.secondShift : null}
									</Typography>
								</Box>
							</StyledListItem>
						);
					})}
				</List>
			</Collapse>
			<Box>
				<Divider />
				<Box display="flex" justifyContent="flex-end">
					<Button
						size="medium"
						onClick={handleExpandClick}
						variant="text"
						sx={{
							'&:hover': {
								backgroundColor: 'transparent',
							},
						}}
						endIcon={
							expandedState ? (
								<Icon name="expand-less-filled" />
							) : (
								<Icon name="expand-more-filled" />
							)
						}>
						{expandedState ? `${seeLess}` : `${seeMore}`}
					</Button>
				</Box>
			</Box>
		</Stack>
	);
};

export {AppointmentsHours};
